import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, FileCheck, Settings, Rocket, BarChart3, Headphones,
  ArrowRight, CheckCircle, Clock, Users, Shield, AlertTriangle,
  Phone, Mail, ExternalLink
} from 'lucide-react';

const ProcessPage = () => {
  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Discovery & Assessment',
      description: 'We begin with a comprehensive evaluation of your current IT environment, business objectives, and challenges.',
      details: [
        'Infrastructure audit and documentation',
        'Security vulnerability assessment',
        'Business requirements gathering',
        'Stakeholder interviews',
        'Gap analysis and opportunity identification'
      ],
      duration: '1-2 Weeks'
    },
    {
      number: '02',
      icon: FileCheck,
      title: 'Strategy & Planning',
      description: 'Based on our findings, we develop a customized IT roadmap aligned with your business goals and budget.',
      details: [
        'Prioritized recommendations',
        'Technology roadmap development',
        'Budget planning and ROI analysis',
        'Statement of Work (SOW) with scope and deliverables',
        'Risk mitigation strategies'
      ],
      duration: '1 Week'
    },
    {
      number: '03',
      icon: Settings,
      title: 'Implementation',
      description: 'Our team executes the plan with minimal disruption to your operations, keeping you informed every step of the way.',
      details: [
        'Phased implementation approach',
        'Change management support',
        'User training and documentation',
        'Testing and quality assurance',
        'Go-live support'
      ],
      duration: 'Varies'
    },
    {
      number: '04',
      icon: Rocket,
      title: 'Optimization',
      description: 'We fine-tune your systems for optimal performance, ensuring everything works seamlessly together.',
      details: [
        'Performance tuning',
        'Security hardening',
        'Process automation',
        'Integration optimization',
        'User feedback incorporation'
      ],
      duration: 'Ongoing'
    },
    {
      number: '05',
      icon: BarChart3,
      title: 'Monitoring & Management',
      description: '24/7 proactive monitoring and management to prevent issues before they impact your business.',
      details: [
        'Remote Monitoring and Management (RMM)',
        'Proactive maintenance',
        'Security monitoring (SIEM)',
        'Performance analytics',
        'Regular health reports'
      ],
      duration: 'Continuous'
    },
    {
      number: '06',
      icon: Headphones,
      title: 'Ongoing Support & Review',
      description: 'Responsive support when you need it, plus regular strategic reviews to keep your technology aligned with your evolving business.',
      details: [
        'Help desk support (phone, email, portal)',
        'Monthly performance reports',
        'Quarterly business reviews',
        'Technology refresh planning',
        'vCTO/vCISO advisory services'
      ],
      duration: 'Continuous'
    },
  ];

  const priorityLevels = [
    {
      level: 'Priority 1',
      name: 'Urgent',
      response: 'Within 1 hour',
      description: '100% of functions or all users affected',
      color: 'bg-red-500'
    },
    {
      level: 'Priority 2',
      name: 'High',
      response: 'Within 2 hours',
      description: '50-99% of functions or users affected',
      color: 'bg-orange-500'
    },
    {
      level: 'Priority 3',
      name: 'Medium',
      response: 'Within 4 hours',
      description: '25-49% of functions or users affected',
      color: 'bg-yellow-500'
    },
    {
      level: 'Priority 4',
      name: 'Low',
      response: 'Within 8 hours',
      description: '1-24% of functions or users affected',
      color: 'bg-green-500'
    },
  ];

  const supportChannels = [
    {
      icon: Mail,
      title: 'Email Support',
      contact: 'service@mimoit.com',
      note: 'Include clear subject line'
    },
    {
      icon: Phone,
      title: 'Phone Support',
      contact: '201-473-4443',
      note: 'Mon-Fri 8:30 AM - 5:30 PM'
    },
    {
      icon: ExternalLink,
      title: 'Ticket Portal',
      contact: 'support.mimoit.com',
      note: 'Track and manage tickets'
    },
    {
      icon: AlertTriangle,
      title: 'Emergency 24/7',
      contact: '201-473-4443',
      note: 'priority_service@mimoit.com'
    },
  ];

  return (
    <div data-testid="process-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 text-white py-20 md:py-28">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-6">
              Our Process
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              From initial assessment to ongoing optimization, we follow a structured approach that delivers results with personal attention and responsive support.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              How We Work
            </h2>
            <p className="text-lg text-slate-600">
              Our structured approach ensures we understand your needs, implement the right solutions, and continuously optimize your IT environment.
            </p>
          </div>
          
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-mimo-primary via-mimo-secondary to-mimo-accent" />
            
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div 
                  key={step.number}
                  className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`process-step-${index}`}
                >
                  <div className={`${index % 2 === 1 ? 'lg:order-2 lg:text-right' : ''}`}>
                    <div className={`inline-flex items-center gap-3 mb-4 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                      <span className="text-5xl font-extrabold font-heading text-mimo-primary/20">{step.number}</span>
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-mimo-primary to-mimo-accent flex items-center justify-center`}>
                        <step.icon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold font-heading text-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 mb-4">
                      {step.description}
                    </p>
                    <div className="inline-flex items-center gap-2 text-sm font-medium text-mimo-primary">
                      <Clock className="w-4 h-4" />
                      <span>Duration: {step.duration}</span>
                    </div>
                  </div>
                  
                  <div className={`bg-slate-50 rounded-2xl p-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <ul className="space-y-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Support SLA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Support Response Times
            </h2>
            <p className="text-lg text-slate-600">
              When you need help, our dedicated support team responds based on issue priority
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {priorityLevels.map((priority, index) => (
              <div 
                key={priority.level}
                className="bg-white rounded-2xl p-6 shadow-sm"
                data-testid={`priority-${index}`}
              >
                <div className={`w-3 h-3 rounded-full ${priority.color} mb-4`} />
                <p className="text-sm text-slate-500 mb-1">{priority.level}</p>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">{priority.name}</h3>
                <p className="text-mimo-primary font-semibold mb-2">{priority.response}</p>
                <p className="text-sm text-slate-600">{priority.description}</p>
              </div>
            ))}
          </div>

          {/* Support Channels */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-xl font-bold font-heading text-slate-900 mb-6 text-center">How to Get Support</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {supportChannels.map((channel, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-mimo-primary/10 flex items-center justify-center mx-auto mb-4">
                    <channel.icon className="w-6 h-6 text-mimo-primary" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">{channel.title}</h4>
                  <p className="text-mimo-primary font-medium text-sm mb-1">{channel.contact}</p>
                  <p className="text-xs text-slate-500">{channel.note}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-slate-500 mt-6">
              Standard Support Hours: Monday - Friday, 8:30 AM - 5:30 PM ET
            </p>
          </div>
        </div>
      </section>

      {/* Reporting */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Transparent Reporting
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Stay informed with regular reports and reviews that keep you in control of your technology investment.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Monthly Performance Reports</h4>
                    <p className="text-slate-600 text-sm">System health, ticket metrics, and key performance indicators</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Quarterly Business Reviews</h4>
                    <p className="text-slate-600 text-sm">Strategic planning, roadmap updates, and technology recommendations</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Incident Reports</h4>
                    <p className="text-slate-600 text-sm">Detailed analysis and remediation steps for any security or system incidents</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="font-bold font-heading text-slate-900 mb-6">Escalation Path</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-mimo-primary text-white flex items-center justify-center font-bold text-sm">1</div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Submit Support Ticket</p>
                    <p className="text-sm text-slate-500">Response: 1-2 business hours</p>
                  </div>
                </div>
                <div className="w-px h-6 bg-slate-200 ml-4" />
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-mimo-accent text-white flex items-center justify-center font-bold text-sm">2</div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Escalate to Technical Lead</p>
                    <p className="text-sm text-slate-500">Response: 4 business hours</p>
                  </div>
                </div>
                <div className="w-px h-6 bg-slate-200 ml-4" />
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-mimo-secondary text-white flex items-center justify-center font-bold text-sm">3</div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Escalate to Account Manager</p>
                    <p className="text-sm text-slate-500">Response: 8 business hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-mimo-primary to-mimo-accent text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Experience our white-glove approach to IT services. Schedule a free assessment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-mimo-primary hover:bg-slate-100 rounded-full px-8 py-3 font-semibold transition-all inline-flex items-center justify-center gap-2"
            >
              Schedule Free Assessment
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a 
              href="tel:201-473-4443" 
              className="border-2 border-white text-white hover:bg-white hover:text-mimo-primary rounded-full px-8 py-3 font-semibold transition-all"
            >
              Call 201-473-4443
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
